import { THEME_RADIUS_MAX_PX, THEME_REM_BASE } from './tokens';

interface IParsedColor {
  channels: [number, number, number];
  alpha: number;
}

const HEX_RE = /^#?([\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/i;
const CHANNELS_RE = /^(\d{1,3}) (\d{1,3}) (\d{1,3})$/;
const RGB_RE = /^rgb(a?)\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)$/i;

/** 解析 hex / rgb(a) /「r g b」；超长、越界或格式不符返回 null */
function parseColor(value: unknown): IParsedColor | null {
  if (typeof value !== 'string' || value.length > 80) return null;
  const input = value.trim();
  let channels: number[];
  let alpha = 1;
  const hex = HEX_RE.exec(input)?.[1];
  const plain = CHANNELS_RE.exec(input);
  const rgb = RGB_RE.exec(input);
  if (hex) {
    const full = hex.length < 5 ? hex.split('').map((c) => c + c).join('') : hex;
    channels = [0, 2, 4].map((i) => Number.parseInt(full.slice(i, i + 2), 16));
    if (full.length === 8) alpha = Number.parseInt(full.slice(6), 16) / 255;
  } else if (plain) {
    channels = plain.slice(1).map(Number);
  } else if (rgb) {
    // rgb() 不带 alpha、rgba() 必须带 alpha
    if (Boolean(rgb[1]) !== (rgb[5] !== undefined)) return null;
    channels = rgb.slice(2, 5).map(Number);
    if (rgb[5] !== undefined) alpha = Number(rgb[5]);
  } else {
    return null;
  }
  if (channels.some((channel) => channel > 255) || alpha < 0 || alpha > 1) return null;
  return { channels: channels as IParsedColor['channels'], alpha };
}

/** 任意受支持颜色 →「r g b」（忽略透明度）；非法返回 null */
export function toRgbChannels(value: unknown): string | null {
  return parseColor(value)?.channels.join(' ') ?? null;
}

/** 任意受支持颜色 → 旧内核可解析的逗号 rgb() / rgba()；非法返回 null */
export function toCssColor(value: unknown): string | null {
  const parsed = parseColor(value);
  if (!parsed) return null;
  const channels = parsed.channels.join(', ');
  return parsed.alpha === 1 ? `rgb(${channels})` : `rgba(${channels}, ${parsed.alpha})`;
}

/** 设计稿 px → rem 字符串（保留四位小数，与根 uno.config postprocess 一致） */
export const pxToRem = (px: number) => `${Number((px / THEME_REM_BASE).toFixed(4))}rem`;

/**
 * 圆角 → rem 字符串：接受 px 数字、数字字符串、"12px"、"0.32rem"（快照/缓存格式）。
 * 负数、非有限数、超上限一律返回 null
 */
export function toRadiusRem(value: unknown): string | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 && value <= THEME_RADIUS_MAX_PX ? pxToRem(value) : null;
  }
  if (typeof value !== 'string') return null;
  const match = /^(\d+(?:\.\d+)?)(px|rem)?$/.exec(value.trim());
  if (!match) return null;
  const num = Number(match[1]);
  if (match[2] !== 'rem') return toRadiusRem(num);
  return num <= Number((THEME_RADIUS_MAX_PX / THEME_REM_BASE).toFixed(4)) ? `${match[1]}rem` : null;
}
